/**
 * Athos Commerce (formerly Searchspring) Consolidated Client API & Utilities.
 * Exposes window.AthosAPI globally.
 */
(function () {
  "use strict";

  if (window.AthosAPI) return;

  const HISTORY_KEY = "ss-history";
  const MAX_HISTORY = 10;
  const TRENDING_CACHE_KEY = "trending-searches";
  const TRENDING_CACHE_TTL = 24 * 60 * 60 * 1000;

  /**
   * Get the current Athos Site ID.
   *
   * @returns {string} The Athos Site ID.
   */
  function getSiteId() {
    return window.themeSettings?.athosSiteId || "";
  }

  /**
   * Retrieve a cookie value by name.
   *
   * @param {string} name - The name of the cookie.
   * @returns {string|null} The cookie value, or null if not found.
   */
  function getCookie(name) {
    const prefix = `${name}=`;
    const cookies = document.cookie ? document.cookie.split("; ") : [];
    // A host-only and a domain-scoped cookie of the same name can briefly
    // coexist (theme preview, or a migrating legacy cookie). Prefer the
    // longest value, which is the fuller tracking history.
    let best = null;
    for (let i = 0; i < cookies.length; i += 1) {
      if (cookies[i].indexOf(prefix) !== 0) continue;
      const raw = cookies[i].slice(prefix.length);
      if (best === null || raw.length > best.length) best = raw;
    }
    if (best === null) return null;
    try {
      return decodeURIComponent(best);
    } catch (e) {
      return best;
    }
  }

  let _pageLoadId = null;
  let _fallbackUserId = null;
  let _fallbackSessionId = null;

  /**
   * Get common tracking parameters for Athos requests.
   * Missing ids are minted once per page load: the beacon API rejects events
   * with empty userId/sessionId, and the `searchspring-init` values can be
   * absent when storage writes fail (Secure cookies on plain-http previews,
   * private windows, cookie blockers).
   *
   * @returns {object} Object containing tracking parameters.
   */
  function getTrackingParams() {
    if (!_pageLoadId) _pageLoadId = crypto.randomUUID();
    let userId = getCookie("athosUserId");
    if (!userId) {
      if (!_fallbackUserId) _fallbackUserId = crypto.randomUUID();
      userId = _fallbackUserId;
    }
    let sessionId = null;
    try {
      sessionId = sessionStorage.getItem("athosSessionId");
    } catch (e) {
      /* Storage access itself throws when the browser blocks it outright */
    }
    if (!sessionId) {
      if (!_fallbackSessionId) _fallbackSessionId = crypto.randomUUID();
      sessionId = _fallbackSessionId;
    }
    return {
      userId,
      sessionId,
      pageLoadId: _pageLoadId,
      domain: window.location.href,
    };
  }

  /**
   * Read a comma-separated SKU cookie into a deduplicated list, preserving
   * order (most recent first for the viewed-products cookie).
   *
   * @param {string} name - The cookie name.
   * @param {number} limit - Maximum number of SKUs to return.
   * @returns {string[]|undefined} The SKUs, or undefined when none.
   */
  function getCookieSkuList(name, limit) {
    try {
      const raw = getCookie(name);
      if (!raw) return undefined;
      const skus = raw
        .split(",")
        .filter(Boolean)
        .filter((sku, index, self) => self.indexOf(sku) === index)
        .slice(0, limit);
      return skus.length > 0 ? skus : undefined;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Personalisation inputs accepted by every Athos data request: the
   * logged-in shopper id plus the cart and recently-viewed SKU lists that
   * `searchspring-init` maintains. Absent values are omitted so anonymous
   * requests are unchanged.
   *
   * @returns {{ shopper?: string, cart?: string[], lastViewed?: string[] }}
   */
  function getPersonalisationParams() {
    const out = {};
    const shopper = getCookie("athosShopperId");
    if (shopper) out.shopper = shopper;
    const cart = getCookieSkuList("athosCartProducts", 25);
    if (cart) out.cart = cart;
    const lastViewed = getCookieSkuList("athosViewedProducts", 15);
    if (lastViewed) out.lastViewed = lastViewed;
    return out;
  }

  /**
   * Append personalisation inputs to a data-request query string.
   *
   * @param {URLSearchParams} params - The request parameters to extend.
   * @returns {void}
   */
  function applyPersonalisationParams(params) {
    const { shopper, cart, lastViewed } = getPersonalisationParams();
    if (shopper) params.set("shopper", shopper);
    if (cart) params.set("cart", cart.join(","));
    if (lastViewed) params.set("lastViewed", lastViewed.join(","));
  }

  /**
   * Validate an API-supplied redirect and reduce it to a same-origin path.
   *
   * @param {string} redirect - Raw redirect value from merchandising config.
   * @returns {string|null} Safe same-origin path (path + query + hash), or null.
   */
  function sanitiseRedirect(redirect) {
    if (typeof redirect !== "string" || redirect.length === 0) return null;
    try {
      const url = new URL(redirect, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.protocol !== "https:" && url.protocol !== "http:") return null;
      return url.pathname + url.search + url.hash;
    } catch (e) {
      return null;
    }
  }

  /**
   * Formats a monetary amount using a Shopify money format template.
   *
   * @param {number|string} amount - The amount to format.
   * @param {string} format - The Shopify money format string (e.g. "${{amount}}").
   * @param {boolean} [isCents=false] - Whether the amount is in cents.
   * @returns {string} The formatted money string.
   */
  function formatMoney(amount, format, isCents = false) {
    let val = typeof amount === "number" ? amount : parseFloat(amount) || 0;
    if (isCents) val = val / 100;

    /**
     * Insert thousands separators into a numeric string.
     *
     * @param {string} numStr - Number as a string, optionally with a '.' decimal.
     * @param {string} thousands - Thousands separator character.
     * @param {string} decimal - Decimal separator character.
     * @returns {string} Separated number string.
     */
    function addSeparators(numStr, thousands, decimal) {
      const parts = numStr.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = typeof format === "string" ? format.match(placeholder) : null;
    const key = match ? match[1] : "amount";

    let value;
    switch (key) {
      case "amount_no_decimals":
        value = addSeparators(String(Math.round(val)), ",", ".");
        break;
      case "amount_with_comma_separator":
        value = addSeparators(val.toFixed(2), ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = addSeparators(String(Math.round(val)), ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = addSeparators(val.toFixed(2), "'", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = addSeparators(String(Math.round(val)), " ", ".");
        break;
      case "amount_with_space_separator":
        value = addSeparators(val.toFixed(2), " ", ",");
        break;
      case "amount_with_period_and_space_separator":
        value = addSeparators(val.toFixed(2), " ", ".");
        break;
      default:
        value = addSeparators(val.toFixed(2), ",", ".");
    }

    return match ? format.replace(placeholder, value) : value;
  }

  /**
   * Build a srcset string from a Shopify CDN image URL by appending width params.
   * Returns null if the URL doesn't look like a Shopify CDN URL.
   *
   * @param {string} url - The base Shopify CDN image URL.
   * @param {number[]} widths - Array of target widths.
   * @returns {string|null} The srcset string, or null if invalid.
   */
  function buildSrcset(url, widths) {
    if (!url || typeof url !== "string") return null;
    if (
      url.indexOf("cdn.shopify.com") === -1 &&
      url.indexOf("shopifycdn.com") === -1
    )
      return null;

    try {
      const parsed = new URL(url, window.location.origin);
      // Strip legacy filename size suffixes like _100x100 or _small so every
      // candidate derives from the master image rather than upscaling a
      // pre-sized thumbnail (mirrors athos-carousel.js).
      parsed.pathname = parsed.pathname.replace(
        /_(?:\d+x\d*(?:_crop_[a-zA-Z]+)?|small|medium|large|grande|original)(\.[a-zA-Z0-9]+)$/i,
        "$1",
      );
      return widths
        .map(function (w) {
          parsed.searchParams.set("width", String(w));
          return parsed.toString() + " " + w + "w";
        })
        .join(", ");
    } catch (e) {
      // Unparseable URL: fall back to the plain string-append behaviour.
      return widths
        .map(function (w) {
          const separator = url.indexOf("?") !== -1 ? "&" : "?";
          return url + separator + "width=" + w + " " + w + "w";
        })
        .join(", ");
    }
  }

  /**
   * Get search history from local storage.
   * Handles string arrays and legacy objects containing searchQuery.
   *
   * @returns {string[]} The array of history terms.
   */
  function getHistory() {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map(function (item) {
          if (typeof item === "string") return item;
          if (item && typeof item === "object")
            return String(item.searchQuery || item.query || "");
          return "";
        })
        .filter(function (term) {
          return typeof term === "string" && term.length > 0;
        });
    } catch (e) {
      return [];
    }
  }

  /**
   * Save a search query to local storage history.
   *
   * @param {string} query - The query to save.
   */
  function saveToHistory(query) {
    if (!query) return;
    const trimmed = query.trim();
    if (trimmed.length < 3) return;

    try {
      let history = getHistory();
      history = history.filter(function (term) {
        return term.toLowerCase() !== trimmed.toLowerCase();
      });
      history.unshift(trimmed);
      if (history.length > MAX_HISTORY) history = history.slice(0, MAX_HISTORY);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch (e) {
      /* Storage unavailable */
    }
  }

  /**
   * Retrieve cached trending searches from session storage.
   *
   * @returns {string[]|null} The cached queries, or null if expired or missing.
   */
  function getCachedTrending() {
    try {
      const raw = sessionStorage.getItem(TRENDING_CACHE_KEY);
      if (!raw) return null;

      const cached = JSON.parse(raw);
      if (Date.now() - cached.timestamp > TRENDING_CACHE_TTL) {
        sessionStorage.removeItem(TRENDING_CACHE_KEY);
        return null;
      }

      return cached.queries;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save trending searches to session storage cache.
   *
   * @param {string[]} queries - The queries to cache.
   */
  function setCachedTrending(queries) {
    try {
      sessionStorage.setItem(
        TRENDING_CACHE_KEY,
        JSON.stringify({ queries: queries, timestamp: Date.now() }),
      );
    } catch (e) {
      /* Storage full */
    }
  }

  let fetchTrendingPromise = null;

  /**
   * Fetch trending searches from the Athos API.
   *
   * @returns {Promise<string[]>} Promise resolving to array of trending terms.
   */
  async function fetchTrending() {
    const siteId = getSiteId();
    if (!siteId) return [];

    const cached = getCachedTrending();
    if (cached && cached.length > 0) return cached;

    if (fetchTrendingPromise) return fetchTrendingPromise;

    fetchTrendingPromise = (async function () {
      try {
        const url = `https://${encodeURIComponent(siteId)}.a.athoscommerce.net/v1/trending?limit=10&siteId=${encodeURIComponent(siteId)}`;
        const resp = await fetch(url);
        if (!resp.ok) return [];
        const data = await resp.json();

        const queries = data?.trending?.queries;
        if (Array.isArray(queries)) {
          const trendingQueries = queries
            .map(function (q) {
              return q.searchQuery;
            })
            .filter(Boolean);
          setCachedTrending(trendingQueries);
          return trendingQueries;
        }
      } catch (e) {
        /* Network error */
      } finally {
        fetchTrendingPromise = null;
      }
      return [];
    })();

    return fetchTrendingPromise;
  }

  /**
   * Fetch recommendations (default recommended products).
   *
   * @param {object} params
   * @param {string[]} params.profiles - Profiles/tags to query.
   * @param {string[]} [params.products] - Product SKUs.
   * @param {string} [params.shopper] - Authenticated Shopper ID.
   * @param {string[]} [params.lastViewed] - Recently viewed SKUs, most recent first.
   * @param {string[]} [params.cart] - SKUs in the shopper's cart.
   * @returns {Promise<object[]>} Resolves to normalized/raw recommend results.
   */
  async function fetchRecommend({
    profiles,
    products = [],
    shopper,
    lastViewed,
    cart,
  }) {
    const siteId = getSiteId();
    if (!siteId || !profiles || profiles.length === 0) return [];

    try {
      const url = `https://${encodeURIComponent(siteId)}.a.p13n.athoscommerce.net/v1/recommend`;
      const { userId, sessionId, pageLoadId } = getTrackingParams();
      const bodyData = {
        profiles: profiles.map((tag) => ({ tag })),
        products: products,
        userId,
        sessionId,
        pageLoadId,
        beacon: true,
      };
      const isDev =
        window.location.hostname.endsWith(".myshopify.com") ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (isDev) bodyData.test = true;
      const personalisation = getPersonalisationParams();
      const shopperId = shopper || personalisation.shopper;
      const viewed = lastViewed?.length ? lastViewed : personalisation.lastViewed;
      const cartSkus = cart?.length ? cart : personalisation.cart;
      if (shopperId) bodyData.shopper = shopperId;
      if (viewed) bodyData.lastViewed = viewed;
      if (cartSkus) bodyData.cart = cartSkus;

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
      });
      if (!resp.ok) return [];
      const data = await resp.json();

      return normaliseRecommendProfiles(data, profiles);
    } catch (e) {
      return [];
    }
  }

  /**
   * Normalises the recommendation API response into per-profile objects.
   *
   * @param {object|object[]} data - The raw API response.
   * @param {string[]} profileTags - The requested profile tags (in order).
   * @returns {{ tag: string, responseId: string, results: object[] }[]}
   */
  function normaliseRecommendProfiles(data, profileTags) {
    const arr = Array.isArray(data) ? data : (data?.recommendations ?? []);
    return arr.map(function (profile, i) {
      return {
        tag: profile.tag || profileTags[i] || "",
        responseId: profile.responseId || "",
        results: Array.isArray(profile.results) ? profile.results : [],
      };
    });
  }

  /**
   * Map standard recommend result properties into a simple object structure.
   *
   * @param {object} r - Individual recommend item.
   * @returns {object} Simple representation of product mapping, including the
   *   identity fields (id, uid, parentId, sku) the beacon mappers require.
   */
  function normaliseProductResult(r) {
    const core = r.mappings?.core || {};
    const attrs = r.attributes || {};
    return {
      id: r.id || "",
      uid: r.uid || core.uid || r.id || "",
      parentId: core.parentId || r.parentId || "",
      sku: r.sku || core.sku || "",
      name: core.name || r.name || r.title || "",
      price: core.price || core.msrp || r.price || 0,
      imageUrl: core.imageUrl || core.thumbnailImageUrl || r.image || "",
      thumbnailImageUrl:
        core.thumbnailImageUrl || core.imageUrl || r.image || "",
      handle: attrs.handle || r.handle || "",
      url: core.url || r.url || "",
    };
  }

  /**
   * Fetch query suggestions.
   *
   * @param {object} params
   * @param {string} params.query - The search query.
   * @param {number} [params.limit=4] - The maximum products/suggestions to fetch.
   * @param {AbortSignal} [params.signal] - Abort controller signal.
   * @param {boolean} [params.suggestionsOnly=false] - Whether to skip product fallback search.
   * @returns {Promise<{suggestions: string[], products: object[], totalResults: number}>}
   */
  async function fetchSuggest({
    query,
    limit = 4,
    signal,
    suggestionsOnly = false,
  }) {
    const siteId = getSiteId();
    if (!siteId || !query) return { ok: false, error: true };

    try {
      const suggestUrl = `https://${encodeURIComponent(siteId)}.a.athoscommerce.net/v1/suggest?siteId=${encodeURIComponent(siteId)}&q=${encodeURIComponent(query)}&suggestionCount=${limit}`;

      const resp = await fetch(suggestUrl, { signal });
      if (!resp.ok) return { ok: false, error: true };
      const data = await resp.json();

      const suggestionTerms = [];
      const products = [];
      let totalResults = 0;

      const suggestedQueries = data?.suggested?.queries;
      if (Array.isArray(suggestedQueries)) {
        suggestedQueries.forEach(function (q) {
          if (q.searchQuery) suggestionTerms.push(q.searchQuery);
        });
      }
      const alternatives = data?.alternatives;
      if (Array.isArray(alternatives)) {
        alternatives.forEach(function (q) {
          if (q.text) suggestionTerms.push(q.text);
        });
      }

      const results = data?.results;
      if (Array.isArray(results)) {
        products.push(...results.slice(0, limit));
        totalResults = data.pagination?.totalResults || results.length;
      }

      let responseId = data?.responseId ?? null;
      let redirect = data?.merchandising?.redirect ?? null;
      let banners = data?.merchandising?.banners ?? [];
      let rawResults = results ?? [];

      if (!suggestionsOnly && products.length === 0) {
        const autocompleteProducts = await fetchAutocomplete({
          query,
          resultsPerPage: limit,
          signal,
        });
        if (autocompleteProducts.aborted) {
          return { ok: false, aborted: true };
        }
        if (autocompleteProducts.ok) {
          if (Array.isArray(autocompleteProducts.products)) {
            products.push(...autocompleteProducts.products);
          }
          totalResults = autocompleteProducts.totalResults || totalResults;
          if (autocompleteProducts.responseId)
            responseId = autocompleteProducts.responseId;
          if (autocompleteProducts.redirect)
            redirect = autocompleteProducts.redirect;
          if (autocompleteProducts.banners?.length > 0)
            banners = autocompleteProducts.banners;
          if (autocompleteProducts.rawResults?.length > 0)
            rawResults = autocompleteProducts.rawResults;
        }
      }

      return {
        ok: true,
        suggestions: suggestionTerms.slice(0, limit),
        products: products.slice(0, limit),
        totalResults: totalResults,
        responseId,
        redirect,
        banners,
        rawResults,
      };
    } catch (err) {
      if (err.name === "AbortError") return { ok: false, aborted: true };
      return { ok: false, error: true };
    }
  }

  /**
   * Fetch product search results.
   *
   * @param {object} params
   * @param {string} params.query - The search query.
   * @param {number} [params.resultsPerPage=24] - Max results per page.
   * @param {number} [params.page=1] - The page number to fetch.
   * @param {AbortSignal} [params.signal] - Abort controller signal.
   * @returns {Promise<{products: object[], totalResults: number, totalPages: number, perPage: number}>}
   */
  async function fetchSearch({
    query,
    resultsPerPage = 24,
    page = 1,
    sort,
    filters,
    signal,
  }) {
    const siteId = getSiteId();
    if (!siteId) return { ok: false, error: true };

    try {
      const { userId, sessionId, pageLoadId, domain } = getTrackingParams();

      const isDev =
        window.location.hostname.endsWith(".myshopify.com") ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      const params = new URLSearchParams({
        resultsFormat: "json",
        q: query,
        resultsPerPage,
        page,
        userId,
        sessionId,
        pageLoadId,
        domain,
        beacon: "true",
      });

      if (isDev) {
        params.set("test", "true");
      }
      applyPersonalisationParams(params);

      if (sort) {
        params.set("sort", sort);
      }

      if (filters) {
        Object.entries(filters).forEach(function ([key, val]) {
          if (Array.isArray(val)) {
            val.forEach(function (v) {
              params.append(key, v);
            });
          } else {
            params.set(key, val);
          }
        });
      }

      const url = `https://${encodeURIComponent(siteId)}.a.athoscommerce.net/v1/search?${params.toString()}`;

      const resp = await fetch(url, { signal });
      if (!resp.ok) return { ok: false, error: true };
      const data = await resp.json();

      const products = [];
      let totalResults = 0;
      let totalPages = 0;
      let perPage = resultsPerPage;

      const results = data?.results;
      if (Array.isArray(results)) {
        products.push(...results);
        totalResults = data.pagination?.totalResults || results.length;
        totalPages = data.pagination?.totalPages || 1;
        perPage = data.pagination?.perPage || resultsPerPage;
      }

      return {
        ok: true,
        products,
        totalResults,
        totalPages,
        perPage,
        responseId: data?.responseId ?? null,
        redirect: data?.merchandising?.redirect ?? null,
        banners: data?.merchandising?.banners ?? [],
        rawResults: results ?? [],
        facets: data?.facets ?? [],
        sorting: data?.sorting ?? null,
      };
    } catch (err) {
      if (err.name === "AbortError") return { ok: false, aborted: true };
      return { ok: false, error: true };
    }
  }

  /**
   * Fetch product autocomplete results.
   *
   * @param {object} params
   * @param {string} params.query - The search query.
   * @param {number} [params.resultsPerPage=4] - Max results per page.
   * @param {AbortSignal} [params.signal] - Abort controller signal.
   * @returns {Promise<{products: object[], totalResults: number}>}
   */
  async function fetchAutocomplete({ query, resultsPerPage = 4, signal }) {
    const siteId = getSiteId();
    if (!siteId) return { ok: false, error: true };

    try {
      const { userId, sessionId, pageLoadId, domain } = getTrackingParams();
      const isDev =
        window.location.hostname.endsWith(".myshopify.com") ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      const params = new URLSearchParams({
        q: query,
        source: "input",
        resultsPerPage,
        userId,
        sessionId,
        pageLoadId,
        domain,
        beacon: "true",
      });
      if (isDev) {
        params.set("test", "true");
      }
      applyPersonalisationParams(params);

      const url = `https://${encodeURIComponent(siteId)}.a.athoscommerce.net/v1/autocomplete?${params.toString()}`;

      const resp = await fetch(url, { signal });
      if (!resp.ok) return { ok: false, error: true };
      const data = await resp.json();

      const products = [];
      let totalResults = 0;

      const results = data?.results;
      if (Array.isArray(results)) {
        products.push(...results);
        totalResults = data.pagination?.totalResults || results.length;
      }

      return {
        ok: true,
        products,
        totalResults,
        responseId: data?.responseId ?? null,
        redirect: data?.merchandising?.redirect ?? null,
        banners: data?.merchandising?.banners ?? [],
        rawResults: results ?? [],
      };
    } catch (err) {
      if (err.name === "AbortError") return { ok: false, aborted: true };
      return { ok: false, error: true };
    }
  }

  /**
   * Fetch a page of category/collection results.
   *
   * @param {object} params
   * @param {string} params.handle - The category collection handle.
   * @param {number} [params.page=1] - The page number.
   * @param {number} [params.perPage=24] - Page size.
   * @param {string} [params.sort] - Sort parameter.
   * @param {object} [params.filters] - Object containing extra query filters.
   * @param {AbortSignal} [params.signal] - Abort controller signal.
   * @returns {Promise<object>} The raw API JSON payload.
   */
  async function fetchCategory({
    handle,
    page = 1,
    resultsPerPage = 24,
    sort,
    filters,
    signal,
  }) {
    const siteId = getSiteId();
    if (!siteId) return { ok: false, error: true };

    const { userId, sessionId, pageLoadId, domain } = getTrackingParams();

    const isDev =
      window.location.hostname.endsWith(".myshopify.com") ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const params = new URLSearchParams({
      resultsPerPage,
      page,
      "bgfilter.collection_handle": handle,
      domain,
      userId,
      sessionId,
      pageLoadId,
      beacon: "true",
      resultsFormat: "json",
      siteId,
    });

    if (isDev) {
      params.set("test", "true");
    }
    applyPersonalisationParams(params);

    if (sort) {
      params.set("sort", sort);
    }

    if (filters) {
      Object.entries(filters).forEach(function ([key, val]) {
        if (Array.isArray(val)) {
          val.forEach(function (v) {
            params.append(key, v);
          });
        } else {
          params.set(key, val);
        }
      });
    }

    const url = `https://${encodeURIComponent(siteId)}.a.athoscommerce.net/v1/category?${params.toString()}`;

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal,
      });
      if (!response.ok) return { ok: false, error: true };
      const data = await response.json();
      return { ok: true, data };
    } catch (err) {
      if (err.name === "AbortError") return { ok: false, aborted: true };
      return { ok: false, error: true };
    }
  }

  /**
   * Allow a CSS colour value only if it contains no property-injection characters.
   *
   * @param {string} value - Candidate colour string from the API.
   * @param {string} fallback - Colour to use when the candidate is unsafe.
   * @returns {string} A safe CSS colour value.
   */
  function safeColour(value, fallback) {
    return typeof value === "string" &&
      /^[#a-zA-Z0-9(),.%\s-]+$/.test(value.trim())
      ? value.trim()
      : fallback;
  }

  /**
   * Recover human-readable banner/badge text from a feed value that may
   * arrive HTML-escaped and/or wrapped in serialised JSON — including JSON
   * the feed has truncated mid-string (e.g. '[&quot;30% Off Code: X').
   *
   * @param {string} value - Candidate text from the API.
   * @returns {string} The recovered text, or '' when nothing readable remains.
   */
  function cleanBannerText(value) {
    if (typeof value !== "string") return "";
    let text = value
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&")
      .trim();
    if (text.startsWith("[") || text.startsWith("{")) {
      try {
        const parsed = JSON.parse(text);
        const first = Array.isArray(parsed) ? parsed[0] : parsed;
        text = typeof first === "string" ? first : "";
      } catch (_e) {
        // Truncated JSON: salvage the first element's readable text.
        text = text
          .replace(/^[[{]\s*"?/, "")
          .split('","')[0]
          .replace(/"?\s*[\]}]?$/, "");
      }
    }
    return text.trim();
  }

  const presentmentPriceCache = {};

  /**
   * Resolve the shopper's presentment context, or null when prices need no
   * conversion (base-currency shopper, or the Shopify globals are missing).
   *
   * @returns {{ country: string }|null}
   */
  function getPresentmentContext() {
    const currency = window.Shopify?.currency;
    const shopCurrency = window.themeSettings?.shopCurrency;
    const country = window.Shopify?.country;
    if (!currency?.active || !shopCurrency || currency.active === shopCurrency)
      return null;
    if (typeof country !== "string" || !/^[A-Z]{2}$/.test(country)) return null;
    return { country };
  }

  /**
   * Look up a previously fetched presentment price for a product handle.
   *
   * @param {string} handle - Product handle.
   * @returns {{ price: number, compareAt: number, currencyCode: string }|null} Amounts in cents.
   */
  function getPresentmentPrice(handle) {
    return presentmentPriceCache[handle] || null;
  }

  /**
   * Fetch exact presentment-currency prices for a set of product handles via
   * the Storefront API. Athos indexes feed prices in the store's base
   * currency, so market shoppers (e.g. AED on the AU store) need real
   * contextual prices — including Markets rounding rules and per-market
   * adjustments — to match the PDP. Results are cached for the session;
   * handles Shopify does not recognise cache as null so they are not refetched.
   *
   * @param {object} params
   * @param {string[]} params.handles - Product handles to price.
   * @param {AbortSignal} [params.signal] - Abort controller signal.
   * @returns {Promise<object>} `{ ok: true, prices }` where `prices` is the
   *   handle→price cache, or null when no conversion is needed;
   *   `{ ok: false, aborted: true }` / `{ ok: false, error: true }` otherwise.
   */
  async function fetchPresentmentPrices({ handles, signal } = {}) {
    const context = getPresentmentContext();
    if (!context) return { ok: true, prices: null };
    const token = window.themeSettings?.storefrontApiToken;
    if (!token) return { ok: false, error: true };

    const batch = [];
    (Array.isArray(handles) ? handles : []).forEach(function (handle) {
      if (typeof handle !== "string" || !/^[a-z0-9][a-z0-9._-]*$/i.test(handle))
        return;
      if (handle in presentmentPriceCache || batch.includes(handle)) return;
      if (batch.length < 50) batch.push(handle);
    });
    if (batch.length === 0) return { ok: true, prices: presentmentPriceCache };

    const fields =
      "handle " +
      "priceRange { minVariantPrice { amount currencyCode } } " +
      "compareAtPriceRange { maxVariantPrice { amount currencyCode } }";
    const query =
      "query @inContext(country: " +
      context.country +
      ") { " +
      batch
        .map(function (handle, i) {
          return (
            "p" +
            i +
            ": product(handle: " +
            JSON.stringify(handle) +
            ") { " +
            fields +
            " }"
          );
        })
        .join(" ") +
      " }";

    // Grid renders await this fetch, so a stalled Storefront API must never
    // hold the page — cap the request at 5s when no caller signal is given.
    let timeoutId = null;
    let effectiveSignal = signal;
    if (!effectiveSignal) {
      const controller = new AbortController();
      timeoutId = setTimeout(function () {
        controller.abort();
      }, 5000);
      effectiveSignal = controller.signal;
    }

    try {
      const res = await fetch(
        (window.Shopify.routes?.root || "/") + "api/2026-04/graphql.json",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({ query }),
          signal: effectiveSignal,
        },
      );
      if (!res.ok) return { ok: false, error: true };
      const data = await res.json();
      const nodes = data?.data || {};
      batch.forEach(function (handle, i) {
        const node = nodes["p" + i];
        const min = node?.priceRange?.minVariantPrice;
        if (!min) {
          presentmentPriceCache[handle] = null;
          return;
        }
        const compare = node.compareAtPriceRange?.maxVariantPrice;
        presentmentPriceCache[handle] = {
          price: Math.round(parseFloat(min.amount) * 100) || 0,
          compareAt: compare
            ? Math.round(parseFloat(compare.amount) * 100) || 0
            : 0,
          currencyCode: min.currencyCode || "",
        };
      });
      return { ok: true, prices: presentmentPriceCache };
    } catch (err) {
      if (err.name === "AbortError") return { ok: false, aborted: true };
      return { ok: false, error: true };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  window.AthosAPI = {
    getSiteId,
    getCookie,
    getTrackingParams,
    getPersonalisationParams,
    formatMoney,
    getPresentmentPrice,
    fetchPresentmentPrices,
    buildSrcset,
    getHistory,
    saveToHistory,
    getCachedTrending,
    setCachedTrending,
    fetchTrending,
    fetchRecommend,
    normaliseRecommendProfiles,
    normaliseProductResult,
    fetchSuggest,
    fetchSearch,
    fetchAutocomplete,
    fetchCategory,
    sanitiseRedirect,
    safeColour,
    cleanBannerText,
  };
})();
